import type { MessageKey } from "@brandex/i18n";
import { LOCALES, type Locale } from "@brandex/i18n";
import { Avatar, Button, Logo, Spinner, ThemeToggle, cn } from "@brandex/ui";
import {
  Activity,
  BarChart3,
  Briefcase,
  CalendarDays,
  Globe,
  Clapperboard,
  Inbox,
  LayoutDashboard,
  LogOut,
  MapPin,
  Megaphone,
  Newspaper,
  Radar,
  Scale,
  ShieldCheck,
  Stethoscope,
  ThumbsUp,
  Users,
  Palette,
  UserRound,
  Share2,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { useBrandex, useT } from "../context";
import { NotificationBell } from "./bell";
import { RefreshButton } from "./refresh";
import { useMe, useMyOrganizations, usePractice, useSignOut, useStaffAccount } from "../queries";
import { ViewSiteButton } from "../views/website";

/** Each language named in itself. */
const LOCALE_NAMES: Record<Locale, string> = { en: "English", bn: "বাংলা" };

/**
 * Restores the session (refresh cookie on web, keychain on desktop) before
 * rendering protected content; redirects to sign-in when there is none.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { api, nav } = useBrandex();
  const t = useT();
  const [ready, setReady] = useState(api.isAuthenticated());
  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    void api.restoreSession().then((ok) => {
      if (cancelled) return;
      if (ok) setReady(true);
      else nav.navigate("/auth/login");
    });
    return () => {
      cancelled = true;
    };
  }, [api, nav, ready]);
  if (!ready) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label={t("common.loading")} />
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Sends a doctor who has not finished onboarding to the wizard. Renders the
 * dashboard once the practice exists (or for members of an onboarded org).
 */
export function OnboardingGate({ children }: { children: ReactNode }) {
  const { nav } = useBrandex();
  const t = useT();
  const orgs = useMyOrganizations();
  const org = orgs.data?.[0];
  const practice = usePractice(org?.organization.id);
  const needsOnboarding = org !== undefined && org.role === "owner" && practice.data === null;
  // A BrandEx staff account has no practice: it belongs in the admin panel.
  const staff = useStaffAccount(orgs.isSuccess && org === undefined);
  useEffect(() => {
    if (needsOnboarding) nav.navigate("/onboarding");
  }, [needsOnboarding, nav]);
  if (staff.data === true) return <StaffAccountNotice />;
  if (orgs.isPending || (org !== undefined && practice.isPending) || needsOnboarding || staff.isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label={t("common.loading")} />
      </div>
    );
  }
  return <>{children}</>;
}

/** Shown instead of the doctor dashboard when a staff account signs in here. */
function StaffAccountNotice() {
  const t = useT();
  const { adminUrl, platform } = useBrandex();
  const signOut = useSignOut();
  return (
    <div className="grid min-h-dvh place-items-center px-4">
      <div className="glass grid w-full max-w-md gap-4 rounded-[var(--radius-xl)] p-8">
        <Logo />
        <h1 className="font-display text-2xl font-bold">{t("staffAccount.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("staffAccount.body")}</p>
        <div className="flex flex-wrap gap-2">
          {adminUrl !== undefined ? (
            <Button onClick={() => void platform.openExternal(`${adminUrl.replace(/\/$/, "")}/login`)}>{t("staffAccount.open")}</Button>
          ) : null}
          <Button variant="outline" onClick={() => signOut.mutate(false)}>{t("common.signOut")}</Button>
        </div>
      </div>
    </div>
  );
}

interface NavItem {
  href: string;
  key: MessageKey;
  icon: typeof Inbox;
  /** Not yet backed by the API; the page states its release honestly. */
  later?: boolean;
}

export const WORKSPACE_NAV: NavItem[] = [
  { href: "/app", key: "nav.month", icon: LayoutDashboard },
  { href: "/app/inbox", key: "nav.inbox", icon: Inbox, later: true },
  { href: "/app/research", key: "nav.research", icon: Radar, later: true },
  { href: "/app/schedule", key: "nav.schedule", icon: CalendarDays },
  { href: "/app/studio", key: "nav.studio", icon: Clapperboard, later: true },
  { href: "/app/content", key: "nav.content", icon: ThumbsUp },
  { href: "/app/profile", key: "nav.portfolio", icon: UserRound },
  { href: "/app/articles", key: "nav.articles", icon: Newspaper },
  { href: "/app/social", key: "nav.social", icon: Share2 },
  { href: "/app/brand", key: "nav.brand", icon: Palette },
  { href: "/app/website", key: "nav.website", icon: Globe },
  { href: "/app/visitors", key: "nav.visitors", icon: Activity },
  { href: "/app/package", key: "nav.package", icon: Briefcase },
  { href: "/app/ads", key: "nav.ads", icon: Megaphone, later: true },
  { href: "/app/google", key: "nav.google", icon: MapPin, later: true },
  { href: "/app/bookings", key: "nav.bookings", icon: Stethoscope },
  { href: "/app/report", key: "nav.report", icon: BarChart3 },
  { href: "/app/ethics", key: "nav.ethics", icon: Scale, later: true },
];

export const SETTINGS_NAV: NavItem[] = [
  { href: "/app/team", key: "nav.team", icon: Users },
  { href: "/app/security", key: "nav.security", icon: ShieldCheck },
];

/** Dashboard layout shared by web and desktop: glass sidebar on navy. */
export function DashboardShell({ children }: { children: ReactNode }) {
  const { nav, locale, setLocale } = useBrandex();
  const t = useT();
  const me = useMe();
  const orgs = useMyOrganizations();
  const practice = usePractice(orgs.data?.[0]?.organization.id);
  const signOut = useSignOut();
  const { Link } = nav;

  const item = ({ href, key, icon: Icon, later }: NavItem) => {
    const active = href === "/app" ? nav.pathname === "/app" : nav.pathname.startsWith(href);
    return (
      <li key={href}>
        <Link
          href={href}
          aria-current={active ? "page" : undefined}
          className={cn(
            "group flex items-center gap-3 whitespace-nowrap rounded-[var(--radius-md)] px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
            active && "bg-azure-500/20 text-foreground shadow-[inset_0_0_0_1px_rgb(77_155_255/0.35)]",
          )}
        >
          <Icon className={cn("h-4 w-4", active && "text-azure-400")} aria-hidden />
          <span className="flex-1">{t(key)}</span>
          {later === true ? <span className="h-1.5 w-1.5 rounded-full bg-ink-500" aria-hidden /> : null}
        </Link>
      </li>
    );
  };

  return (
    <div className="flex min-h-[calc(100dvh-var(--titlebar-h,0px))] flex-col gap-4 p-3 md:flex-row md:p-4">
      <aside className="glass relative z-30 flex print:hidden shrink-0 flex-col rounded-[var(--radius-xl)] md:sticky md:top-[calc(1rem+var(--titlebar-h,0px))] md:h-[calc(100dvh-2rem-var(--titlebar-h,0px))] md:w-64">
        <div className="flex h-16 items-center justify-between px-5">
          <Logo />
          {/* Auto margin keeps refresh (desktop app) and the bell beside the phone sign-out button. */}
          <div className="ml-auto flex items-center gap-1">
            <RefreshButton />
            <NotificationBell />
          </div>
          {/* The account footer is desktop-only; phones get sign-out here. */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            loading={signOut.isPending}
            onClick={() => signOut.mutate(false)}
          >
            <LogOut className="h-4 w-4" aria-hidden />
            {t("common.signOut")}
          </Button>
        </div>
        <nav aria-label="Main" className="flex-1 overflow-y-auto px-3 pb-3">
          <ul className="flex gap-1 overflow-x-auto md:grid md:overflow-visible">{WORKSPACE_NAV.map(item)}</ul>
          <p className="mb-1 mt-5 hidden px-3 text-[11px] font-medium uppercase tracking-[0.14em] text-ink-500 md:block">
            {t("nav.settings")}
          </p>
          <ul className="mt-1 flex gap-1 md:grid">{SETTINGS_NAV.map(item)}</ul>
        </nav>
        <div className="hidden border-t border-border p-4 md:block">
          {me.data !== undefined ? (
            <div className="mb-3 flex items-center gap-3">
              <Avatar name={me.data.display_name} />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{me.data.display_name}</p>
                <p className="truncate text-xs text-muted-foreground">{me.data.email}</p>
              </div>
            </div>
          ) : null}
          {practice.data ? <ViewSiteButton practice={practice.data} className="mb-3 w-full" /> : null}
          <ThemeToggle className="mb-3" />
          <div className="flex items-center justify-between gap-2">
            {/* A native <select> popup is drawn by the OS with system fonts,
                which may lack Bangla (tofu on some Linux/WebView setups);
                in-page buttons render with the bundled Hind Siliguri. */}
            <div role="radiogroup" aria-label={t("common.language")} className="inline-flex rounded-[var(--radius-pill)] border border-border p-0.5">
              {LOCALES.map((l) => (
                <button
                  key={l}
                  type="button"
                  role="radio"
                  lang={l}
                  aria-checked={locale === l}
                  onClick={() => setLocale(l)}
                  className={cn(
                    "h-7 rounded-full px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground",
                    locale === l && "bg-primary text-primary-foreground hover:text-primary-foreground",
                  )}
                >
                  {LOCALE_NAMES[l]}
                </button>
              ))}
            </div>
            <Button variant="ghost" size="sm" loading={signOut.isPending} onClick={() => signOut.mutate(false)}>
              <LogOut className="h-4 w-4" aria-hidden />
              {t("common.signOut")}
            </Button>
          </div>
        </div>
      </aside>
      <main id="main" className="min-w-0 flex-1 px-1 py-2 md:px-6 md:py-4">{children}</main>
    </div>
  );
}
